import { useEffect } from 'react'
import { useConsent } from '@/providers/consent-provider'

// HubSpot analytics/tracking loader. This is the portal-wide tracking script
// (hs-scripts.com) that records page views, sessions, and sets HubSpot's
// `__hstc`/`hubspotutk`/`__hssc` cookies — distinct from the per-page form
// embeds on /contact and /joinus.
//
// HubSpot is categorized under MARKETING consent (see the cookie banner and
// privacy policy), so this loads only after the visitor grants marketing
// consent and is removed again if they revoke it — keeping us compliant with
// the consent model and the published privacy policy.
const HS_SCRIPT_ID = 'hs-script-loader'
const HS_PORTAL_ID = '243241691'
const HS_SCRIPT_SRC = `//js-na2.hs-scripts.com/${HS_PORTAL_ID}.js`

/** Cookies HubSpot's tracker sets; cleared on consent revoke. */
const HS_COOKIES = ['__hstc', 'hubspotutk', '__hssc', '__hssrc']

function disableHubSpotTracking() {
  // HubSpot's tracker keeps running in SPA sessions once initialized. This API
  // explicitly disables tracking for the current page lifecycle.
  const w = window as Window & { _hsq?: unknown[] }

  // Avoid creating a queued "doNotTrack" before HubSpot is present; otherwise a
  // later consent grant in the same SPA session could be immediately opted out.
  if (!Array.isArray(w._hsq)) return

  w._hsq.push(['doNotTrack'])
}

function clearHubSpotCookies() {
  const domain = window.location.hostname.replace(/^www\./, '')
  for (const name of HS_COOKIES) {
    // Clear for the current host and the registrable domain (HubSpot sets some
    // cookies on the dot-prefixed parent domain).
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${domain}`
  }
}

export function HubSpotTracking() {
  const { categories } = useConsent()
  const marketing = categories.marketing

  useEffect(() => {
    if (marketing) {
      if (document.getElementById(HS_SCRIPT_ID)) return
      const script = document.createElement('script')
      script.id = HS_SCRIPT_ID
      script.type = 'text/javascript'
      script.async = true
      script.defer = true
      script.src = HS_SCRIPT_SRC
      document.head.appendChild(script)
    } else {
      // Consent absent or revoked — explicitly opt out in the active session,
      // then remove the loader + clear cookies.
      disableHubSpotTracking()
      document.getElementById(HS_SCRIPT_ID)?.remove()
      clearHubSpotCookies()
    }
  }, [marketing])

  return null
}
